import * as z from "zod";
import { HandleableError } from "@shared/utils/errors/handleableError";
import {
  AbortError,
  ApiError,
  UnexpectedResponseError
} from "@shared/utils/errors/apiError";
import { CommonDto } from "@infrastructure/clients/http/common.dto";
import { AuthStore } from "@infrastructure/stores/auth.store";
import { logApiRequest, logApiResponseError } from "@shared/utils/logger/apiLogger";

export interface ErrorSchema<T extends z.ZodRawShape> {
  model: z.ZodObject<T>;
  get: (data: z.infer<z.ZodObject<T>>) => [title: string, description: string];
}

export const handleError = async (e: unknown): Promise<HandleableError> => {
  if (e instanceof HandleableError) e.handle();

  // Обработка AxiosError
  if (e && typeof e === 'object' && 'isAxiosError' in e && e.isAxiosError && 'response' in e && e.response) {
    const axiosError = e as any;
    const response = axiosError.response;
    const errorData = response.data;
    const request = axiosError.config || axiosError.request;

    // Логируем запрос
    const requestData = request?.data;
    let dataPreview: any = undefined;
    if (requestData) {
      if (typeof requestData === 'string') {
        dataPreview = requestData.substring(0, 500);
      } else if (requestData instanceof FormData) {
        dataPreview = '[FormData]';
      } else {
        dataPreview = requestData;
      }
    }
    
    // Логируем запрос с временем
    logApiRequest({
      method: request?.method?.toUpperCase() || 'UNKNOWN',
      url: request?.url || 'unknown',
      baseURL: request?.baseURL,
      fullURL: request?.baseURL && request?.url ? `${request.baseURL}${request.url}` : undefined,
      headers: request?.headers,
      params: request?.params,
      data: dataPreview,
    });

    // Логируем ответ с ошибкой с временем
    logApiResponseError({
      method: request?.method?.toUpperCase() || 'UNKNOWN',
      url: request?.url || 'unknown',
      status: response.status,
      statusText: response.statusText,
      fullURL: request?.baseURL && request?.url ? `${request.baseURL}${request.url}` : undefined,
      requestData: request?.data,
      errorData: errorData,
    });

    // Обработка 401 Unauthorized - запускаем автоматическую переавторизацию
    if (response.status === 401) {
      console.log("[handleError] 401 Unauthorized detected, triggering re-authentication...");
      
      // Устанавливаем статус reauth для прозрачной переавторизации
      const currentAuthState = AuthStore.auth;
      if (currentAuthState.state === "authenticated") {
        console.log("[handleError] Setting reauth state (was authenticated)");
        AuthStore.setAuth({ 
          state: "reauth", 
          previousUser: currentAuthState.user 
        });
      } else {
        console.log("[handleError] Setting reauthRequired (was not authenticated)");
        AuthStore.setAuth({ state: "anonymous", reauthRequired: true });
      }
      
      // Запускаем переавторизацию асинхронно
      // Используем динамический импорт, чтобы избежать циклических зависимостей
      import("@domain/services/auth.service").then(({ AuthService }) => {
        AuthService.startBackgroundReauth().catch((err) => {
          console.error("[handleError] Background reauth failed:", err);
        });
      }).catch((err) => {
        console.error("[handleError] Failed to import AuthService:", err);
      });
    }

    // Для 422 ошибок валидации пытаемся извлечь детали из ответа
    if (response.status === 422) {
      let errorMessage = "Ошибка валидации данных";
      
      // Формат FastAPI/Pydantic: {detail: [{type, loc, msg, input}]}
      if (errorData?.detail && Array.isArray(errorData.detail)) {
        const errors = errorData.detail.map((err: any) => {
          const field = Array.isArray(err.loc) && err.loc.length > 1 
            ? err.loc.slice(1).join('.') 
            : Array.isArray(err.loc) && err.loc.length > 0
              ? err.loc[0]
              : 'unknown';
          const msg = err.msg || err.message || 'Field required';
          return `${field}: ${msg}`;
        });
        errorMessage = errors.join(', ');
      } 
      // Формат CommonDto: {details: "message"}
      else if (errorData?.details) {
        errorMessage = errorData.details;
      } 
      // Формат {data: {errors: {field: "message"}}}
      else if (errorData?.data?.errors && typeof errorData.data.errors === 'object') {
        const errors = Object.entries(errorData.data.errors)
          .map(([field, msg]) => `${field}: ${msg}`)
          .join(', ');
        errorMessage = errors || errorMessage;
      } 
      // Простые форматы
      else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      } 
      // Если массив ошибок напрямую
      else if (Array.isArray(errorData) && errorData.length > 0) {
        errorMessage = errorData.map((err: any) => 
          err.msg || err.message || err.path?.join('.') || JSON.stringify(err)
        ).join(', ');
      } 
      // Если объект с данными
      else if (errorData?.data && typeof errorData.data === 'object') {
        errorMessage = JSON.stringify(errorData.data);
      }
      
      console.log(`[handleError] 422 error message extracted:`, errorMessage);
      return new ApiError("Ошибка валидации", errorMessage, response.status);
    }

    const data = CommonDto.ApiError.safeParse(errorData);
    if (data.success) {
      // Для ошибок 500 проверяем data.errors (может содержать "initData expired" и т.д.)
      let errorMessage = data.data.details;
      if (response.status === 500 && errorData?.data?.errors) {
        // Если есть data.errors, используем его как более детальное сообщение
        const errorsDetail = typeof errorData.data.errors === 'string' 
          ? errorData.data.errors 
          : JSON.stringify(errorData.data.errors);
        errorMessage = errorsDetail || errorMessage;
      }
      return new ApiError("Ошибка", errorMessage, response.status);
    } else {
      // Для ошибок 500 проверяем data.errors
      let errorMessage = errorData?.details || errorData?.message || errorData?.error || "Неизвестная ошибка";
      if (response.status === 500 && errorData?.data?.errors) {
        const errorsDetail = typeof errorData.data.errors === 'string' 
          ? errorData.data.errors 
          : JSON.stringify(errorData.data.errors);
        errorMessage = errorsDetail || errorMessage;
      }
      return new ApiError("Ошибка", errorMessage, response.status);
    }
  }

  if (e instanceof Response) {
    let errorData;
    try {
      errorData = await e.json();
    } catch {
      return new UnexpectedResponseError(e.url, e, errorData);
    }

    // Для 422 ошибок валидации пытаемся извлечь детали из ответа
    if (e.status === 422) {
      // Пытаемся найти сообщение об ошибке в разных форматах
      let errorMessage = "Ошибка валидации данных";
      if (errorData?.details) {
        errorMessage = errorData.details;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      } else if (Array.isArray(errorData) && errorData.length > 0) {
        // Если это массив ошибок валидации
        errorMessage = errorData.map((err: any) => 
          err.message || err.path?.join('.') || JSON.stringify(err)
        ).join(', ');
      }
      return new ApiError("Ошибка валидации", errorMessage, e.status);
    }

    const data = CommonDto.ApiError.safeParse(errorData);
    if (data.success) {
      return new ApiError("Ошибка", data.data.details, e.status);
    } else {
      // Если не удалось распарсить как стандартную ошибку, но есть какое-то сообщение
      const errorMessage = errorData?.message || errorData?.error || errorData?.details || "Неизвестная ошибка";
      return new ApiError("Ошибка", errorMessage, e.status);
    }
  } else if (e instanceof Error) {
    if (e.name === "AbortError") {
      return new AbortError();
    }

    // Обработка таймаутов
    if (e.message.includes("timeout") || e.message.includes("Network Error")) {
      return new ApiError("Превышено время ожидания", 
        "Операция заняла слишком много времени. Попробуйте еще раз через несколько минут.");
    }

    return new ApiError("Ошибка", e.message);
  }

  return new ApiError("Ошибка", "Произошла ошибка при обработке запроса");
};

